import { Calendar, Copy, MapPin, Search, XCircle } from 'react-feather';
import { ChangeEvent, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import times from 'lodash/times';

import { GetEventsQuery as GetEvents, PurchaseStatus } from '__generated__/graphql';

import { assetUrl, encodeUrl, resizedImageUrl } from '../../common/helpers';
import DateRange from '../../common/Common/DateRange';
import GetEventsQuery from './GetEventsQuery';
import OptionsDropdown, { Option } from '../../common/Common/OptionsDropdown';
import UI from '../../common/UI';
import useDocumentTitle from '../../common/useDocumentTitle';
import useDuplicateEventModal from './useDuplicateEventModal';
import useLocale from '../../common/useLocale';
import useMediaDevice from '../../common/useMediaDevice';
import useProject from '../useProject';
import useQuery from '../../api/useQuery';
import useUrlState from '../../common/useUrlState';

type Event = GetEvents['project']['events']['data'][0];

const EventsPage = () => {
  const { t } = useTranslation();
  const project = useProject();
  const device = useMediaDevice();

  useDocumentTitle(t('events'));

  const [{ page, ...urlState }, setUrlState] = useUrlState({
    parse: (rawParams, { boolean, string, integer }) => ({
      search: string(rawParams.search),
      page: integer(rawParams.page, 1),
      new: boolean(rawParams.new),
    }),
    onChange: (urlState) => {
      const search = urlState.search || '';
      setSearch(search);

      if (searchRef.current) {
        searchRef.current.value = search;
      }
    },
  });

  const [search, setSearch] = useState<string>(urlState.search);
  const searchRef = useRef<HTMLInputElement>();

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setUrlState((urlState) => ({ ...urlState, page: null, search: event.target.value || null }), true);
  };

  const resetSearch = () => {
    setSearch('');
    searchRef.current.value = '';
    setUrlState((urlState) => ({ ...urlState, search: null }), true);
  };

  const perPage = 9;

  const { data, loading, refetch } = useQuery(
    GetEventsQuery,
    {
      variables: {
        project_id: project.id,
        search,
        page,
        per_page: perPage,
      },
    },
  );

  const events = data?.project.events;
  const totalCount = data?.project.all_events.total;

  const placeholderCount = events ? (3 - (events.data.length % 3)) % 3 : 0;
  const showSearch = totalCount > perPage;

  if (!events) {
    return <UI.PageLoader />;
  }

  if (totalCount === 0) {
    return (
      <UI.Section sc={{ compact: true, mt: { md: 8 } }}>
        <CreateEventCard />
      </UI.Section>
    );
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.FlexContainer sc={{ justifyContent: 'space-between' }}>
          <UI.SectionHeading>
            {t('events')}
          </UI.SectionHeading>
          {(device.isSmall || !showSearch) && (
            <UI.NavButton to={`/${project.id}/events/add`} sc={{ brand: 'secondary' }}>
              {t('create_an_event')}
            </UI.NavButton>
          )}
        </UI.FlexContainer>

        {showSearch && (
          <UI.FormGrid sc={{ columns: ['1fr', '1fr 2fr'] }}>
            <UI.InputGroup>
              <UI.InputWrapper>
                <UI.Span>
                  <UI.Icon>
                    <Search />
                  </UI.Icon>
                </UI.Span>
                <UI.DebouncedInput
                  type="text"
                  value={search}
                  onChange={handleSearch}
                  placeholder={t('search_events')}
                  ref={searchRef}
                  sc={{ px: 5 }}
                />
                {search && (
                  <UI.Span>
                    <UI.A onClick={() => resetSearch()} sc={{ color: 'gray.400' }}>
                      <UI.Icon>
                        <XCircle />
                      </UI.Icon>
                    </UI.A>
                  </UI.Span>
                )}
              </UI.InputWrapper>
            </UI.InputGroup>
            {!device.isSmall && (
              <UI.Div sc={{ textAlign: 'right' }}>
                <UI.NavButton to={`/${project.id}/events/add`} sc={{ brand: 'secondary' }}>
                  {t('create_an_event')}
                </UI.NavButton>
              </UI.Div>
            )}
          </UI.FormGrid>
        )}

        {events.total === 0 && (
          <UI.Card sc={{ placeholder: true, textAlign: 'center', muted: true }}>
            {t('no_results')}
          </UI.Card>
        )}

        <UI.AutoGridContainer sc={{ loading }}>
          {events.data.map((event) => (
            <EventCard event={event} key={event.id} onDuplicate={refetch} />
          ))}
          {!device.isSmall && times(placeholderCount, (index) => (
            <UI.Card key={index} sc={{ placeholder: true }} />
          ))}
        </UI.AutoGridContainer>

        {events && events.last_page > 1 && (
          <UI.Div>
            <UI.Pager pageCount={events.last_page} scroll />
          </UI.Div>
        )}
      </UI.GridContainer>
    </UI.Section>
  );
};

interface EventCardProps {
  event: Event;
  onDuplicate: () => void;
}

const EventCard = ({ event, onDuplicate }: EventCardProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const { formatInteger } = useLocale();

  const [openDuplicateEventModal] = useDuplicateEventModal({
    event,
    onSuccess: onDuplicate,
  });

  return (
    <UI.NavCard
      to={`/${project.id}/events/${event.id}/`}
      ariaLabel={event.title}
      sc={{
        hasPanels: true, flex: { direction: 'column', justifyContent: 'space-between' }, mb: 0,
      }}
    >
      <EventCardContainer sc={{ pt: 4, textAlign: 'center' }}>
        <OptionsDropdown>
          <Option
            icon={<Copy />}
            label={t('duplicate')}
            onClick={() => openDuplicateEventModal()}
          />
        </OptionsDropdown>
        <UI.ProfilePic sc={{ background: event.logo ? 'white' : undefined, size: { sm: 50, md: 70 }, mb: 3 }}>
          {event.logo && <img src={resizedImageUrl(event.logo.url, 300)} alt={event.title} />}
          {!event.logo && <UI.Span><UI.Span>{event.title.charAt(0)}</UI.Span></UI.Span>}
        </UI.ProfilePic>

        <UI.H3 sc={{ mb: 2 }}>
          <UI.Truncate>{event.title}</UI.Truncate>
        </UI.H3>

        <UI.Div sc={{ small: true, color: 'gray.500' }}>
          <UI.Icon>
            <Calendar />
          </UI.Icon>
          {' '}
          <DateRange start={event.start_date} end={event.end_date} />
        </UI.Div>

        <UI.Div sc={{ small: true, color: 'gray.500' }} style={{ minHeight: 18 }}>
          {event.location?.title && (
            <>
              <UI.Icon>
                <MapPin />
              </UI.Icon>
              {' '}
              {event.location?.title}
            </>
          )}
        </UI.Div>

        <UI.Strong sc={{ mt: 2, block: true, muted: !event.active }}>
          <UI.StatusIndicator sc={{ background: event.active ? 'success' : 'gray.600', mr: 1 }} />
          {' '}
          {t(event.active ? 'live' : 'offline')}
        </UI.Strong>
      </EventCardContainer>
      <UI.Div sc={{ color: 'gray.500', textAlign: 'center', small: true }}>
        <UI.Link
          to={`/${project.id}/participants?${encodeUrl({
            event: event.id,
            statuses: [PurchaseStatus.complete, PurchaseStatus.incomplete],
          })}`}
          sc={{ secondary: true, color: 'gray.500' }}
        >
          <Trans
            i18nKey="n_registrations_emphasized"
            count={event.registrations_count}
            values={{
              value: formatInteger(event.registrations_count),
            }}
          >
            <UI.Strong />
          </Trans>
        </UI.Link>
        {(event.allow_teams || event.teams_count > 0) && (
          <UI.Link
            to={`/${project.id}/participants/teams?events[0]=${event.id}`}
            sc={{ secondary: true, color: 'gray.500', ml: 4 }}
          >
            <Trans
              i18nKey="n_teams_emphasized"
              count={event.teams_count}
              values={{
                value: formatInteger(event.teams_count),
              }}
            >
              <UI.Strong />
            </Trans>
          </UI.Link>
        )}
      </UI.Div>
    </UI.NavCard>
  );
};

const EventCardContainer = styled(UI.Div)`
  ${({ theme }) => css`
    position: relative;

    ${UI.DropdownOptionsContainer} {
      position: absolute;
      top: ${theme.gutter}px;
      right: ${theme.gutter}px;
    }
  `}
`;

export const CreateEventCard = () => {
  const project = useProject();
  const { t } = useTranslation();

  return (
    <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}>
      <img src={assetUrl('images/event.svg')} alt="" height={120} />
      <UI.H3 sc={{ mb: 2 }}>{t('create_your_first_event')}</UI.H3>
      <UI.Paragraph>{t('events_get_started')}</UI.Paragraph>
      <UI.NavButton
        to={`/${project.id}/events/add`}
        sc={{ brand: 'secondary' }}
      >
        {t('create_an_event')}
      </UI.NavButton>
    </UI.Card>
  );
};
export default EventsPage;
